import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import type { ErrorAction, ErrorActionRegistry } from '@/shared/types/error-action'
import {
  extractApiError,
  reportErrorToSentry,
  showErrorToast
} from '@/shared/utils/handle-api-error'

/**
 * Error handler with actionable toasts. Given a feature's action registry
 * (errorCode → resolver), returns `handleError(error)` that:
 *  - resolves the actions for the error's code (with navigation + cache context),
 *  - shows the rich ErrorToast with those actions,
 *  - reports to Sentry.
 *
 * Drop-in replacement for `handleMutationError` in a mutation's `onError` when the
 * error should offer actions. Without a matching resolver it behaves like the
 * plain handler (message only).
 */
export function useErrorHandler(registry: ErrorActionRegistry = {}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  return useCallback(
    (error: unknown) => {
      const apiError = extractApiError(error)
      const resolver = apiError ? registry[apiError.errorCode] : undefined

      let actions: ErrorAction[] = []
      if (apiError && resolver) {
        actions = resolver({
          apiError,
          navigate,
          queryClient,
          dismiss: () => toast.dismiss()
        })
      }

      showErrorToast(error, actions)
      reportErrorToSentry(error)
    },
    [registry, navigate, queryClient]
  )
}
